// Lead Detail — center column: filter chips, timeline, composer.
// Phase 1 ships the Note channel only; Email/Call/Task/LinkedIn modes are
// rendered but disabled with "Coming soon" hint.
import React, { Component } from "react";
import HelperClass from "../../../../config/helperClass";
import {
  addNote, updateNote, deleteNote,
  listTeamMembers,
} from "../api/leads_api";
import {
  relativeTime, renderNoteBody, safeJson, ACTIVITY_TYPE_STYLES,
} from "./helpers";

const auth = new HelperClass();

function formatActivityDate(ts, withTime = true) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  const opts = { month: "short", day: "numeric", year: "numeric" };
  const date = d.toLocaleDateString(undefined, opts);
  if (!withTime) return date;
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return date + " " + time;
}

function priorityBadgeMeta(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "high") return { label: "High Priority", classes: "badge badge-pill badge-outline-danger" };
  if (p === "med" || p === "medium") return { label: "Medium Priority", classes: "badge badge-pill badge-outline-warning" };
  if (p === "low") return { label: "Low Priority", classes: "badge badge-pill badge-outline-secondary" };
  if (p) return { label: String(priority), classes: "badge badge-pill badge-outline-primary" };
  return null;
}

const FILTERS = [
  { key: "all",    label: "All",      color: "#6b7280", enabled: true },
  { key: "note",   label: "Notes",    color: "#d97706", enabled: true },
  // { key: "stage",  label: "Stage",    color: "#1c5bde", enabled: true },
  { key: "system", label: "System",   color: "#6b7280", enabled: true },
  // Uncomment when channels are live:
  // { key: "email", label: "Emails", color: "#0369a1", enabled: false },
  // { key: "call",  label: "Calls",  color: "#1a7a4a", enabled: false },
  // { key: "task",  label: "Tasks",  color: "#6b3fa0", enabled: false },
];

const COMPOSER_MODES = [
  { key: "note", label: "Note", enabled: true },
  // Uncomment when channels are live:
  // { key: "email",    label: "Email",    enabled: false },
  // { key: "call",     label: "Call",     enabled: false },
  // { key: "task",     label: "Task",     enabled: false },
  // { key: "linkedin", label: "LinkedIn", enabled: false },
];

const VISIBILITY_OPTS = [
  { value: "all",        label: "Whole Team" },
  { value: "internal",   label: "Admin Only" },
  { value: "agent_only", label: "Agents Only" },
];

// ─── Single timeline event row ─────────────────────────────────────────
class EventRow extends Component {
  state = { editing: false, editBody: "", editVisibility: "all", saving: false };

  startEdit = () => {
    const { activity } = this.props;
    this.setState({
      editing: true,
      editBody: activity.body || "",
      editVisibility: (activity.metadata && safeJson(activity.metadata, {}).visibility) || "all",
    });
  };

  cancelEdit = () => this.setState({ editing: false });

  saveEdit = async () => {
    const { activity, onChanged } = this.props;
    const meta = safeJson(activity.metadata, {}) || {};
    if (!meta.note_id) { this.cancelEdit(); return; }
    this.setState({ saving: true });
    const r = await updateNote(auth.getAccount(), auth.getToken(), {
      note_id: meta.note_id, body: this.state.editBody, visibility: this.state.editVisibility,
    });
    this.setState({ saving: false, editing: false });
    if (r && r.data && r.data.status === 200 && onChanged) onChanged();
  };

  delete = async () => {
    const { activity, onChanged } = this.props;
    const meta = safeJson(activity.metadata, {}) || {};
    if (!meta.note_id) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Delete this note?")) return;
    const r = await deleteNote(auth.getAccount(), auth.getToken(), meta.note_id);
    if (r && r.data && r.data.status === 200 && onChanged) onChanged();
  };

  renderBody(text, mentions) {
    const parts = renderNoteBody(text, mentions);
    if (!Array.isArray(parts)) return parts;
    return parts.map(function (p, i) {
      if (typeof p === "string") return <span key={i}>{p}</span>;
      return <span key={i} className="ld-mention">@{p.mention}</span>;
    });
  }

  render() {
    const { activity, leadPriority } = this.props;
    const { editing, editBody, editVisibility, saving } = this.state;
    const style = ACTIVITY_TYPE_STYLES[activity.type] || ACTIVITY_TYPE_STYLES.system;
    const meta = safeJson(activity.metadata, {}) || {};
    const isNote = activity.type === "note" && meta.note_id;
    const titleText = String(activity.title || "").toLowerCase();
    const bodyText = String(activity.body || "").toLowerCase();
    const isStage = activity.type === "stage" || titleText.includes("stage") || bodyText.includes("stage");
    const mentions = meta.mentions || [];
    const visibility = meta.visibility || null;
    const visLabel = visibility === "internal" ? "Internal" : visibility === "all" ? "All" : visibility === "agent_only" ? "Agent" : null;
    const createdDate = activity.created_at ? formatActivityDate(activity.created_at) : "";
    const displayTime = !isNote ? ((activity.type === "system" || isStage) ? createdDate : relativeTime(activity.created_at)) : null;

    return (
      <div className={"ld-tl-event" + (isStage ? " ld-tl-stage-event" : "")}>
        <div className="ld-tl-icon" style={{ background: style.bg, color: style.color }}>
          {style.label[0]}
        </div>
        <div className="ld-tl-body">
          <div className={"ld-tl-meta" + (isStage ? " ld-tl-meta-stage" : "") }>
            <span className="ld-tl-meta-author">{activity.type === "system" ? "System" : (activity.created_by_name || activity.created_by || "System")}</span>
            <span>·</span>
            {!isNote ? <span title={activity.created_at}>{displayTime}</span> : null}
            {!isNote ? <span>·</span> : null}
            <span>{style.label}</span>
            {visLabel ? (
              <span className="ld-tl-vis-pill"
                style={{ background: style.bg, color: style.color }}>{visLabel}</span>
            ) : null}
            {isNote ? (
              <span className="ld-tl-actions">
                <button className="ld-tl-action" onClick={this.startEdit}>Edit</button>
                <button className="ld-tl-action" onClick={this.delete}>Delete</button>
              </span>
            ) : null}
          </div>
          {isStage ? (
            <div className="ld-tl-stage-body">
              <div>
                {activity.title ? <div className="ld-tl-title">{activity.title}</div> : <div className="ld-tl-title">{style.label}</div>}
                {activity.body ? <div className="ld-tl-text">{this.renderBody(activity.body, mentions)}</div> : null}
              </div>
            </div>
          ) : editing ? (
            <div className="ld-tl-edit">
              <textarea className="ld-tl-edit-text" value={editBody}
                onChange={(e) => this.setState({ editBody: e.target.value })} />
              <div className="ld-tl-edit-row">
                <select value={editVisibility}
                  onChange={(e) => this.setState({ editVisibility: e.target.value })}
                  style={{ padding: "3px 6px", fontSize: 12 }}>
                  {VISIBILITY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <span style={{ flex: 1 }} />
                <button className="ld-field-btn ld-field-btn-cancel" onClick={this.cancelEdit} disabled={saving}>Cancel</button>
                <button className="ld-field-btn ld-field-btn-save" onClick={this.saveEdit} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <React.Fragment>
              {activity.title ? <div className="ld-tl-title">{activity.title}</div> : null}
              {activity.body ? <div className="ld-tl-text">{this.renderBody(activity.body, mentions)}</div> : null}
              {isNote && createdDate ? <div className="ld-tl-note-footer">{createdDate}</div> : null}
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }
}

// ─── Composer (Phase 1 = Note mode only) ──────────────────────────────
class Composer extends Component {
  state = {
    mode: "note",
    body: "",
    visibility: "all",
    sending: false,
    mentionOpen: false,
    mentionQuery: "",
    mentionAnchor: 0,
    mentions: [],
    hasDraft: false,
    draftSavedAt: null,
  };

  backdropRef = React.createRef();
  textareaRef = React.createRef();

  draftKey() { return "ld_note_draft_" + (this.props.lead_id || "x"); }

  componentDidMount() {
    this.loadTeam();
    // Restore any saved draft for this lead.
    try {
      var saved = localStorage.getItem(this.draftKey());
      if (saved) {
        var d = JSON.parse(saved);
        this.setState({ body: d.body || "", visibility: d.visibility || "all", hasDraft: true, draftSavedAt: d.savedAt || null });
      }
    } catch (e) { /* ignore */ }
  }

  componentWillUnmount() {
    if (this._draftTimer) clearTimeout(this._draftTimer);
  }

  loadTeam = async () => {
    const r = await listTeamMembers(auth.getAccount(), auth.getToken());
    if (r && r.data && r.data.status === 200) {
      this.team = r.data.data || [];
    } else { this.team = []; }
  };

  setMode = (m) => {
    const cfg = COMPOSER_MODES.filter(function (x) { return x.key === m; })[0];
    if (!cfg || !cfg.enabled) {
      if (this.props.onToast) this.props.onToast("Coming soon", false);
      return;
    }
    this.setState({ mode: m });
  };

  onInput = (e) => {
    const body = e.target.value;
    // Sync backdrop scroll
    if (this.backdropRef.current && this.textareaRef.current) {
      this.backdropRef.current.scrollTop = this.textareaRef.current.scrollTop;
    }
    const pos = e.target.selectionStart || 0;
    // Find an @-trigger token: last "@" before cursor, no space after it.
    const before = body.substring(0, pos);
    const at = before.lastIndexOf("@");
    if (at !== -1 && /^@\w*$/.test(before.substring(at))) {
      this.setState({
        body, mentionOpen: true,
        mentionQuery: before.substring(at + 1).toLowerCase(),
        mentionAnchor: at,
      });
    } else {
      this.setState({ body, mentionOpen: false });
    }
    // Auto-save draft after 1500ms of inactivity
    if (this._draftTimer) clearTimeout(this._draftTimer);
    if (body.trim()) {
      this._draftTimer = setTimeout(this.autoSaveDraft, 1500);
    }
  };

  pickMention = (m) => {
    const { body, mentionAnchor } = this.state;
    const before = body.substring(0, mentionAnchor);
    const afterCursor = body.substring(mentionAnchor).replace(/^@\w*/, "");
    const inserted = "@" + m.name + " ";
    const next = before + inserted + afterCursor;
    const newMentions = this.state.mentions.slice();
    if (!newMentions.some(function (x) { return x.accountno === m.accountno; })) {
      newMentions.push({ name: m.name, accountno: m.accountno });
    }
    this.setState({ body: next, mentionOpen: false, mentions: newMentions });
  };

  clear = () => {
    if (this._draftTimer) clearTimeout(this._draftTimer);
    this.setState({ body: "", mentions: [], mentionOpen: false, hasDraft: false, draftSavedAt: null });
    try { localStorage.removeItem(this.draftKey()); } catch (e) { /* ignore */ }
  };

  autoSaveDraft = () => {
    const { body, visibility } = this.state;
    if (!body.trim()) return;
    var savedAt = Date.now();
    try {
      localStorage.setItem(this.draftKey(), JSON.stringify({ body, visibility, savedAt }));
    } catch (e) { /* ignore */ }
    this.setState({ hasDraft: true, draftSavedAt: savedAt });
  };

  saveDraft = () => {
    if (this._draftTimer) clearTimeout(this._draftTimer);
    this.autoSaveDraft();
    if (this.props.onToast) this.props.onToast("Draft saved", false);
  };

  send = async () => {
    const { lead_id, onAdded } = this.props;
    const { body, visibility, mentions } = this.state;
    if (!body.trim()) return;
    this.setState({ sending: true });
    const r = await addNote(auth.getAccount(), auth.getToken(), {
      lead_id, body, visibility, mentions,
      creator_name: auth.getName() || auth.getCompName() || auth.getEmail() || "",
    });
    this.setState({ sending: false });
    if (r && r.data && r.data.status === 200) {
      if (this._draftTimer) clearTimeout(this._draftTimer);
      this.setState({ body: "", mentions: [], hasDraft: false, draftSavedAt: null });
      try { localStorage.removeItem(this.draftKey()); } catch (e) { /* ignore */ }
      if (onAdded) onAdded(r.data.data && r.data.data.note);
    }
  };

  onScroll = () => {
    if (this.backdropRef.current && this.textareaRef.current) {
      this.backdropRef.current.scrollTop = this.textareaRef.current.scrollTop;
    }
  };

  renderBackdrop() {
    const { body, mentions } = this.state;
    if (!body) return <div className="ld-cmp-backdrop" ref={this.backdropRef}>&nbsp;</div>;
    // Escape HTML entities so the div doesn't interpret any user-typed HTML.
    var escaped = body
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    // Highlight each picked @mention. Sort longest-first to avoid partial replacements.
    var highlighted = escaped;
    var sorted = mentions.slice().sort(function(a, b) { return b.name.length - a.name.length; });
    sorted.forEach(function(m) {
      var safe = m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      // Match @Name at word boundary or end-of-string, case-insensitive.
      var re = new RegExp("@" + safe + "(?=\\s|$)", "g");
      highlighted = highlighted.replace(re, function(match) {
        return '<mark class="ld-cmp-mention-hl">' + match + "</mark>";
      });
    });
    // dangerouslySetInnerHTML is safe here — body is user text we HTML-escaped above;
    // only the known @Name values are wrapped in <mark> by us.
    return (
      <div
        className="ld-cmp-backdrop"
        ref={this.backdropRef}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: highlighted + "&nbsp;" }}
      />
    );
  }

  renderMentions() {
    if (!this.state.mentionOpen || !this.team || !this.team.length) return null;
    const q = this.state.mentionQuery;
    const matches = this.team.filter(function (m) {
      return !q || (m.name && m.name.toLowerCase().indexOf(q) !== -1);
    }).slice(0, 8);
    if (!matches.length) return null;
    return (
      <div className="ld-mention-picker">
        {matches.map((m, i) => (
          <div key={m.accountno || i} className="ld-mention-item"
            onMouseDown={(e) => { e.preventDefault(); this.pickMention(m); }}>
            <span className="ld-mention-avatar">{(m.name || "?").charAt(0).toUpperCase()}</span>
            <span>{m.name || m.email || m.accountno}</span>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { mode, body, visibility, sending, hasDraft, draftSavedAt } = this.state;
    var draftLabel = null;
    if (hasDraft && draftSavedAt) {
      var sec = Math.round((Date.now() - draftSavedAt) / 1000);
      draftLabel = sec < 10 ? "Draft saved just now" : sec < 60 ? "Draft saved " + sec + "s ago" : "Draft saved " + Math.round(sec / 60) + "m ago";
    } else if (hasDraft) {
      draftLabel = "Draft restored";
    }
    const enabledModes = COMPOSER_MODES.filter(function (m) { return m.enabled; });
    return (
      <div className="ld-composer">
        {enabledModes.length > 1 ? (
          <div className="ld-composer-tabs">
            {enabledModes.map((m) => (
              <button key={m.key}
                className={"ld-cmp-tab" + (mode === m.key ? " ld-cmp-tab-on" : "")}
                onClick={() => this.setMode(m.key)}>
                {m.label}
              </button>
            ))}
          </div>
        ) : null}
        {mode === "note" ? (
          <div className="ld-internal-banner">
            <div className="ld-internal-banner-l">
              <span>📝</span>
              <div>
                <div className="ld-internal-banner-ttl">Add Note</div>
                <div className="ld-internal-banner-sub">
                  Use <strong>@</strong> to mention a teammate.
                </div>
              </div>
            </div>
            <div className="ld-internal-banner-r">
              <label className="ld-internal-banner-vislbl">Visible to:</label>
              <select className="ld-internal-vis-sel" value={visibility}
                onChange={(e) => this.setState({ visibility: e.target.value })}>
                {VISIBILITY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        ) : null}
        <div className="ld-composer-body">
          <div className="ld-cmp-wrapper">
            {this.renderBackdrop()}
            <textarea
              ref={this.textareaRef}
              className="ld-cmp-text"
              placeholder="Type your note… use @ to mention a teammate"
              value={body}
              onChange={this.onInput}
              onScroll={this.onScroll}
            />
          </div>
          {this.renderMentions()}
        </div>
        <div className="ld-composer-foot">
          {hasDraft ? <span className="ld-cmp-draft-badge">✦ {draftLabel || "Draft saved"}</span> : null}
          <span className="ld-cmp-spacer" />
          <button className="ld-field-btn ld-field-btn-cancel" onClick={this.clear} disabled={sending}>Discard</button>
          <button className="ld-field-btn ld-field-btn-cancel" onClick={this.saveDraft} disabled={sending || !body.trim()}>Save Draft</button>
          <button className="ld-field-btn ld-field-btn-save" onClick={this.send} disabled={sending || !body.trim()}>
            {sending ? "Saving…" : "Add Note"}
          </button>
        </div>
      </div>
    );
  }
}

// ─── Center container ────────────────────────────────────────────────
export default class Center extends Component {
  state = { filter: "all" };

  setFilter = (f) => {
    this.setState({ filter: f });
  };

  render() {
    const { lead, activities, onActivitiesChanged, onToast } = this.props;
    const { filter } = this.state;
    const filtered = filter === "all" ? activities : activities.filter(function (a) { return a.type === filter; });
    return (
      <div className="ld-center">
        <div className="ld-tl-filterbar">
          <div className="ld-tl-filters">
            {FILTERS.map((f) => (
              <button key={f.key}
                className={"ld-tl-chip" + (filter === f.key ? " ld-tl-chip-on" : "")}
                style={{ "--chip-color": f.color }}
                onClick={() => this.setFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: "#9ca3af" }}>
            {filtered.length} event{filtered.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="ld-timeline">
          {filtered.length === 0 ? (
            <div className="ld-tl-empty">No activity yet. Add a note below to get started.</div>
          ) : filtered.map((a) => (
            <EventRow key={a.id} activity={a} leadPriority={lead.priority} onChanged={onActivitiesChanged} />
          ))}
        </div>

        <Composer lead_id={lead.id} onAdded={onActivitiesChanged} onToast={onToast} />
      </div>
    );
  }
}
